import React, {useState, useEffect, useRef, ChangeEvent} from 'react';
import './EstimateTreeDetailComponent.css'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent, CardHeader,
  CircularProgress,
  Dialog, DialogActions,
  DialogTitle, Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {PhotoEdit, saveImageEdit} from "../../../shared/services/image-service";
import {EstimateTreeDto, UpdateEstimateTreeDto, WorkItemType} from "../../../shared/dtos/estimate-tree.dto";
import {
  createTreeOperation,
  deleteTree,
  deleteTreeOperation,
  getTreeOperationMethods,
  getTreeOperations,
  updateTree,
  updateTreeOperation,
  updateTreeOperationMethods
} from "../estimate-tree.service"
import {EstimateTreeOperationDto, InputEstimateTreeOperationDto} from "../../../shared/dtos/estimate-tree-operation.dto";
import {useNavigate} from "react-router-dom";
import {enqueueSnackbar} from "notistack";
import {logError} from "../../../shared/services/logger.service";
import {DrawableImage, PHOTO_DIALOG_PAPER} from "../DrawableImage";
import {CenteredCircularSpinner} from "../../../shared/components/CenteredCircularSpinner";
import {ExternalCompanyDto} from "../../../shared/dtos/company.dto";

import {
  uploadImagesFromFiles
} from "../../../shared/misc/image-helpers";
import { EstimateTreeOperationMethodDto } from '../../../shared/dtos/estimate-tree-operation-method.dto';
import { getAllOperations } from '../../equipment/services/operation-method.service';
import { CustomOperationMethodDto } from '../../equipment/dtos/operation-method.dto';
import {LibraryEquipmentChoiceDto, LibraryWorkItemStateDto, SaveLibraryWorkItemResponseDto} from '../../../shared/dtos/comparable-library.dto';
import {WorkPlanCatalogDto} from '../../../shared/dtos/work-plan-catalog.dto';
import {FiguresDraft, figuresOf, missingFigures, sameFigures} from '../../admin/library/figures/figures';
import {OperationFiguresFields} from '../../admin/library/figures/OperationFiguresFields';
import {EquipmentRowsEditor, equipmentTypesUsed} from '../../admin/library/figures/EquipmentRowsEditor';
import { EstimateTreeDetailImagesControls } from './EstimateTreeDetailImagesControls';
import { EstimateTreeDetailDataFields, exactValueIsValid, InferredFacts } from './EstimateTreeDetailDataFields';
import { EstimateTreeDetailOperations, noteWith, operationIsValid, orphanedOperationText } from './EstimateTreeDetailOperations';

import { SaveAndAddNewComponent } from '../shared-components/SaveAndAddNewComponent';
import {PhotoContextFields} from '../../admin/library/PhotoContextFields';
import {workItemTypeLabel} from '../../admin/library/library.format';
import {MissingFact, missingFacts} from '../../admin/library/missingFacts';
import {ReviewStrip} from '../../admin/library/ReviewStrip';
import {ScrollPastCue} from '../../admin/library/ScrollPastCue';

export interface ImagePreview {
  id: number,
  s3Url: string,
}

export interface EstimateTreeProps {
  tree: EstimateTreeDto,
  treeLocalId: number,
  company: ExternalCompanyDto,
  onNewWorkItem: (updatedTree: EstimateTreeDto, workItemType: WorkItemType) => void,
  onNewGate: (updatedTree: EstimateTreeDto) => void,
  handleSaveAndReturnToEstimate: (updatedTree: EstimateTreeDto) => void,
  onTreeDelete: () => void,
  mode?: 'edit' | 'read-only',
  /** SQ-500 library preparation: facts only, saved in one request by the caller, with item navigation. */
  preparation?: TreeDetailPreparation,
}

/** What staff edited, with what was saved, so the caller can send only changes. */
export interface PreparationDraft {
  revision: string;
  savedTree: EstimateTreeDto;
  updatedTree: UpdateEstimateTreeDto;
  savedOperations: EstimateTreeOperationDto[];
  operations: InputEstimateTreeOperationDto[];
  operationMethodIds: number[];
  savedOperationMethodIds: number[];
  /** The legacy-record editor's figures (time per operation, equipment), with what was saved. */
  figures: FiguresDraft;
  savedFigures: FiguresDraft;
}

/** What the legacy-record editor needs beside the facts: the item's stored figures and the choices on offer. */
export interface PreparationFigures {
  item?: LibraryWorkItemStateDto;
  equipmentChoices: LibraryEquipmentChoiceDto[];
  crewSize?: number | null;
  laborRatePerHour?: number | null;
  workPlan?: WorkPlanCatalogDto | null;
}

export type PreparationSaveState =
  | {kind: 'clean'} | {kind: 'dirty'} | {kind: 'saving'} | {kind: 'saved'}
  | {kind: 'error', message: string, stale: boolean};

export interface TreeDetailPreparation {
  revision: string;
  /** One request; resolves with the saved tree, or rejects with a message for staff. */
  save: (draft: PreparationDraft) => Promise<SaveLibraryWorkItemResponseDto>;
  /** A stale revision: reload this item from the server. */
  reload: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  /** On the last item of a job that can be approved: continue to the approval. */
  onFinish?: () => void;
  /** The next work item without saving this one; the caller asks about unsaved changes. Shown at the end of the form. */
  onAdvance?: () => void;
  nextLabel?: string;
  /** Names the item onPrevious goes to, for the cue at the top of the form. */
  previousLabel?: string;
  onStateChange?: (state: PreparationSaveState) => void;
  inferred?: InferredFacts | null;
  inferredContext?: Record<number, Record<string, string | number>>;
  /** Bumped by the caller to ask for a save (e.g. "Save" in an unsaved-changes prompt); resolves via onStateChange. */
  saveRequest?: {id: number, then?: () => void};
  /** The legacy-record editor: time per operation and equipment, saved with the facts. */
  figures?: PreparationFigures;
  /**
   * Photos on another work item of the estimate that this one may take (SQ-630): a stump split
   * off a tree by the October 2026 repair shows the tree's photos, and a click links one to the
   * stump (an existing image id in imageIdsToAdd; the save accepts any of the company's images).
   */
  photoSource?: {label: string, images: ImagePreview[]};
  /** The legacy-record editor: delete this operation (the page asks where its price and time go). Offered only with no unsaved changes. */
  onDeleteOperation?: (operationId: number) => void;
  /** The legacy-record editor: add an operation (the page asks where its price and time come from). */
  onAddOperation?: () => void;
  /** The operation to open on, e.g. one just added; the first otherwise. */
  initialOperationId?: number;
  /** SQ-671: change this work item's type (the page asks what each operation becomes). Offered only with no unsaved changes. */
  onChangeType?: () => void;
}

const getUnsavedModificationsLocalStorageKey = (estimateId: number, treeId: number) => `unsavedTree_${estimateId}_${treeId}`;
// Library preparation keeps its own drafts: the ordinary editor fills blanks with 0 defaults.
const getPreparationDraftKey = (estimateId: number, treeId: number) => `libraryDraft_${estimateId}_${treeId}`;

interface StoredTreeState {
  updatedTree: UpdateEstimateTreeDto;
  updatedOperationsDetails: (InputEstimateTreeOperationDto | undefined)[];
  // Only library drafts carry concurrency and method selections; ordinary editing is unchanged.
  revision?: string;
  operationMethodIds?: number[];
  figures?: FiguresDraft;
}

const EstimateTreeDetail: React.FC<EstimateTreeProps> = React.forwardRef((props, ref) => {
  const mode = props.mode ?? 'edit';
  const preparation = props.preparation;
  const draftKey = preparation ? getPreparationDraftKey : getUnsavedModificationsLocalStorageKey;

  const navigate = useNavigate();
  const scrollableRef = useRef<HTMLDivElement>(null);
  const estimateTreeToUpdatedTree = (tree: EstimateTreeDto): UpdateEstimateTreeDto => {
    // Library preparation keeps unknown dimensions unknown instead of 0.
    return {
      ...tree,
      height: preparation ? tree.height : tree.height ?? '0 Feet',
      width: preparation ? tree.width : tree.width ?? '0 Feet',
      trunkDiameter: preparation ? tree.trunkDiameter : tree.trunkDiameter ?? '0 Inches',
      additionalDetails: preparation ? {...(tree.additionalDetails ?? {})} : tree.additionalDetails,
      treeId: tree.id,
      updatedBy: tree.createdBy
    }
  }
  const [preparationState, setPreparationState] = useState<PreparationSaveState>({kind: 'clean'});
  const isLibraryPreparation = !!preparation;
  const canEdit = mode === 'edit' && preparationState.kind !== 'saving';
  const draftRevision = useRef(preparation?.revision ?? '');
  const preparationReady = useRef(false);

  // This setup logic needs to be duplicated in the useEffect so the component re-renders properly, we do it here to make the type system happy
  const [savedTree, setSavedTree] = useState<EstimateTreeDto>(props.tree);
  const [updatedTree, setUpdatedTree] = useState<UpdateEstimateTreeDto>(estimateTreeToUpdatedTree(props.tree));
  const [treeImagePreviews, setTreeImagePreviews] = useState<ImagePreview[]>(savedTree.images.map(image => ({
    id: image.id,
    s3Url: image.imageUrl
  })));
  const [countTreeImagesLoading, setCountTreeImagesLoading] = useState<number>(0);
  const [treeImageIsUpdating, setTreeImageIsUpdating] = useState<boolean>(false);

  const [imageDialogIsOpen, setImageDialogIsOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<ImagePreview>();
  // SQ-572: the full-screen photo, opened from a thumbnail; this window hides behind it like it does behind the editor.
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);

  const [savedOperationsDetails, setSavedOperationsDetails] = useState<EstimateTreeOperationDto[]>([]);
  const [updatedOperationsDetails, setUpdatedOperationsDetails] = useState<(InputEstimateTreeOperationDto | undefined)[]>([]);
  // The legacy-record editor's figures ride beside the facts in the same draft and save.
  const [savedFigures, setSavedFigures] = useState<FiguresDraft>(() => figuresOf(preparation?.figures?.item));
  const [figuresDraft, setFiguresDraft] = useState<FiguresDraft>(() => figuresOf(preparation?.figures?.item));
  // A save elsewhere on the page (the crew, the time distribution) moves this item's stored figures:
  // take them when nothing here is being edited, and keep an edit in progress.
  const storedFiguresKey = JSON.stringify(preparation?.figures?.item ? figuresOf(preparation.figures.item) : null);
  useEffect(() => {
    if (!preparation?.figures?.item) return;
    const stored = figuresOf(preparation.figures.item);
    setSavedFigures(previous => {
      setFiguresDraft(draft => sameFigures(draft, previous) ? stored : draft);
      return stored;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedFiguresKey]);
  const [selectedUpdatedOperationDetails, setSelectedUpdatedOperationDetails_Internal] = useState<InputEstimateTreeOperationDto>();
  const [selectedOperationIndex, setSelectedOperationIndex_Internal] = useState<number>(0);
  const [availableOperationMethods, setAvailableOperationMethods] = useState<CustomOperationMethodDto[]>([]);
  const [savedOperationMethods, setSavedOperationMethods] = useState<EstimateTreeOperationMethodDto[]>([]);
  const [selectedOperationMethodIds, setSelectedOperationMethodIds] = useState<number[]>([]);

  const [operationIsLoading, setOperationIsLoading] = useState<boolean>(false);
  const [operationImageIsUpdating] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isFinishing, setIsFinishing] = useState<boolean>(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  // We use this intermediary object for the active operation because array update change detection is noticeably slower in the UI
  const setSelectedUpdatedOperationDetails = (updater: (prev: InputEstimateTreeOperationDto | undefined) => InputEstimateTreeOperationDto) => {
    setUpdatedOperationsDetails(prevOperations => {
      const newOperations = [...prevOperations];
      const newOrUpdatedOp = updater(prevOperations[selectedOperationIndex]);
      newOperations[selectedOperationIndex] = newOrUpdatedOp;
      setSelectedUpdatedOperationDetails_Internal(newOrUpdatedOp);
      return newOperations;
    });
  };

  const getTreeUnsavedModifications = (estimateId: number, treeId: number) : StoredTreeState | undefined => {
    const localStorageKey = draftKey(estimateId, treeId);

    try {
      const savedStateString = localStorage.getItem(localStorageKey);
      if (savedStateString) {
        const savedState: StoredTreeState = JSON.parse(savedStateString);
        if (savedState.updatedTree && savedState.updatedTree.treeId === props.tree.id) {
          return savedState;
        } else {
          // Clear invalid state if treeId doesn't match
          localStorage.removeItem(localStorageKey);
        }
      }
    } catch (error) {
      // Ensure error is of type Error for logError
      const errorToLog = error instanceof Error ? error : new Error(String(error));
      logError('Error parsing saved tree state from localStorage', { key: localStorageKey }, errorToLog);
      localStorage.removeItem(localStorageKey); // Clear corrupted data
    }
  }

  useEffect(() => {
    setOperationIsLoading(true);

    const unsavedModifications = getTreeUnsavedModifications(props.tree.estimateId, props.tree.id);
    const initialUpdatedTree = unsavedModifications?.updatedTree ?? estimateTreeToUpdatedTree(props.tree);
    const initialOperationsDetails: (InputEstimateTreeOperationDto | undefined)[] = unsavedModifications?.updatedOperationsDetails ?? [];
    console.log(`Retrieved initial operations details: ${JSON.stringify(initialOperationsDetails)}`);
    setSavedTree(props.tree);
    setUpdatedTree(initialUpdatedTree);
    // TODO: We may want to support loading unsaved images from localStorage
    setTreeImagePreviews(props.tree.images.map(image => ({
      id: image.id,
      s3Url: image.imageUrl
    })));

    setSavedOperationsDetails([]);
    setUpdatedOperationsDetails(initialOperationsDetails);

    if (preparation) {
      let active = true;
      preparationReady.current = false;
      // Never attach a newly fetched revision to an older draft (including legacy drafts).
      draftRevision.current = unsavedModifications ? unsavedModifications.revision ?? '' : preparation.revision;
      const storedFigures = figuresOf(preparation.figures?.item);
      setSavedFigures(storedFigures);
      setFiguresDraft(unsavedModifications?.figures ?? storedFigures);
      Promise.all([
        getTreeOperations(navigate, props.tree.estimateId, props.tree.id),
        getAllOperations(navigate),
        getTreeOperationMethods(navigate, props.tree.estimateId, props.tree.id),
      ]).then(([operations, availableMethods, methods]) => {
        if (!active) return;
        const saved = operations.sort((a, b) => a.id - b.id);
        const edited = unsavedModifications?.updatedOperationsDetails ?? saved;
        setSavedOperationsDetails(saved);
        setUpdatedOperationsDetails(edited);
        const initialIndex = Math.max(0, edited.findIndex(op => op?.id !== undefined && op.id === preparation.initialOperationId));
        setSelectedUpdatedOperationDetails_Internal(edited[initialIndex]);
        setSelectedOperationIndex_Internal(initialIndex);
        setAvailableOperationMethods(availableMethods);
        setSavedOperationMethods(methods);
        setSelectedOperationMethodIds(unsavedModifications?.operationMethodIds ?? methods.map(method => method.customOperationMethodId));
        preparationReady.current = true;
        setOperationIsLoading(false);
        if (draftRevision.current !== preparation.revision) {
          reportPreparation({kind: 'error', stale: true,
            message: 'This draft was made against an older version. Reload the item before saving; your draft is kept until you choose to reload.'});
        }
      }).catch(error => {
        if (!active) return;
        setOperationIsLoading(false);
        reportPreparation({kind: 'error', stale: true, message: 'Could not load this item. Reload to try again; your draft is kept.'});
        logError('Error loading library work item', {}, error);
      });
      return () => { active = false; };
    }
    
    // If we didn't have any unsaved operations, fetch them
    if(initialOperationsDetails.length === 0) {
      getTreeOperations(navigate, props.tree.estimateId, props.tree.id)
      .then(operations => {
        const sortedOperations = operations.sort((a, b) => a.id > b.id ? 1 : -1);
        setSavedOperationsDetails(sortedOperations);
        setUpdatedOperationsDetails(sortedOperations);
        setSelectedUpdatedOperationDetails_Internal(sortedOperations[0]);
        setSelectedOperationIndex_Internal(0);
      })
      .catch(e => {
        logError('Error retrieving tree operations', {}, e)
        enqueueSnackbar(`Error retrieving tree operations: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
      .finally(() => setOperationIsLoading(false));
    } else {
      setSelectedUpdatedOperationDetails(() => initialOperationsDetails[0]!)
      setOperationIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tree]);

  // Scroll to top when tree changes (when modal re-opens with new tree)
  useEffect(() => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTop = 0;
    }
  }, [props.tree.id]);

  useEffect(() => {
    if (preparation && (!preparationReady.current || preparationState.kind === 'saving')) return;
    const savedState: StoredTreeState = {
      updatedTree: estimateTreeToUpdatedTree(savedTree),
      updatedOperationsDetails: savedOperationsDetails
    }
    const currentState: StoredTreeState = {
      updatedTree,
      updatedOperationsDetails,
      ...(preparation ? {revision: draftRevision.current, operationMethodIds: selectedOperationMethodIds, figures: figuresDraft} : {}),
    };
    const localStorageKey = draftKey(props.tree.estimateId, props.tree.id);
    try {
      const savedStateString = JSON.stringify(savedState);
      const currentStateString = JSON.stringify(currentState);
      const methodsChanged = JSON.stringify([...selectedOperationMethodIds].sort()) !==
        JSON.stringify(savedOperationMethods.map(method => method.customOperationMethodId).sort());
      const changed = preparation
        ? comparableTree(updatedTree) !== comparableTree(savedState.updatedTree)
          || JSON.stringify(updatedOperationsDetails) !== JSON.stringify(savedOperationsDetails) || methodsChanged
          || !sameFigures(figuresDraft, savedFigures)
        : savedStateString !== JSON.stringify({updatedTree, updatedOperationsDetails});
      if(changed) {
        if(updatedTree.treeId !== props.tree.id) {
          console.error(`*****ERROR, ATTEMPTING TO SAVE STATE FROM TREE ${updatedTree.treeId} TO TREE ${props.tree.id}*****`);
        }
        console.log('Saving state to localStorage', { key: localStorageKey, currentState });
        localStorage.setItem(localStorageKey, currentStateString);
      } else {
        if (preparation && draftRevision.current === preparation.revision) localStorage.removeItem(localStorageKey);
      }
    } catch (error) {
      // Ensure error is of type Error for logError
      const errorToLog = error instanceof Error ? error : new Error(String(error));
      logError('Error saving tree state to localStorage', { key: localStorageKey }, errorToLog);
      enqueueSnackbar('Could not save unsaved changes.', { variant: 'warning' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTree, updatedOperationsDetails, selectedOperationMethodIds, figuresDraft]);

  useEffect(() => {
    if (isLibraryPreparation) return;
    Promise.all([
      getAllOperations(navigate),
      getTreeOperationMethods(navigate, props.tree.estimateId, props.tree.id)
    ])
      .then(([operations, methods]) => {
        setAvailableOperationMethods(operations);
        setSavedOperationMethods(methods);
        setSelectedOperationMethodIds(methods.map(method => method.customOperationMethodId));
      })
      .catch(e => {
        logError('Error fetching operation methods', {}, e);
        enqueueSnackbar('Error fetching operation methods', { variant: 'error', autoHideDuration: 5000 });
      });
  }, [props.tree.id, props.tree.estimateId, navigate, isLibraryPreparation]);

  useEffect(() => {
    console.log(`Selected operation method ids: ${selectedOperationMethodIds}`);
  }, [selectedOperationMethodIds]);

  useEffect(() => {
    console.log(`savedOperationMethods: ${savedOperationMethods}`);
  }, [savedOperationMethods]);

  const updateTreeImagesInState = (newImagePreviews: ImagePreview[]) => {
    setTreeImagePreviews((prevImages) => [
      ...prevImages.filter(image => !newImagePreviews.map(newPreview => newPreview.id).includes(image.id)),
      ...newImagePreviews]);
    setUpdatedTree(prevUpdatedTree => {
      const imagesToAdd = newImagePreviews.filter(preview =>
        !prevUpdatedTree.imageIdsToAdd?.includes(preview.id) && !savedTree.images.map(i => i.id).includes(preview.id)); // We don't want to add images that were updated in place
      // A drawing replaces a photo in place: nothing to link, and no change to record.
      if (imagesToAdd.length === 0) return prevUpdatedTree;
      return {
        ...prevUpdatedTree!,
        imageIdsToAdd: [
            ...prevUpdatedTree!.imageIdsToAdd ?? [], ...imagesToAdd.map(file => file.id)
        ]
      }
    });
  }

  const allOperationsValid = () => {
    return updatedOperationsDetails.every(operationIsValid);
  }

  const handleCompressedTreeImage = async (files: File[]) => {
    await uploadImagesFromFiles(files, setCountTreeImagesLoading, updateTreeImagesInState);
  };

  const handleTreeImageUpdated = async (id: number, edit: PhotoEdit) =>
    genericUpdateImage(id, edit, setTreeImageIsUpdating, (preview: ImagePreview) => updateTreeImagesInState([preview]))

  const genericUpdateImage = async (id: number, edit: PhotoEdit, isUpdatingSetter: (isUpdating: boolean) => void, onUpdateComplete: (preview: ImagePreview) => void) => {
    isUpdatingSetter(true);
    await saveImageEdit(id, edit)
      .then(result => {
        const newImagePreview = {id: result.id, s3Url: result.imageUrl}
        onUpdateComplete(newImagePreview);
        closeImageDialog();
      })
      .catch(e => {
        logError('Error updating image', {}, e);
        enqueueSnackbar(`Error updating image: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
      .finally(() => isUpdatingSetter(false));
  }

  const handleImageUpdated = async (id: number, edit: PhotoEdit) => {
    if(treeImagePreviews.find(image => image.id === id)) {
      return handleTreeImageUpdated(id, edit);
    }
    logError('Attempted to update an image that does not exist in state');
  }

  const handleCrewNotesChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUpdatedTree(tree => ({...tree!, crewNotes: event.target.value}));
  };

  const saveTreeAndExecute = async (loadingSetter: (isLoading: boolean) => void, action: (tree: EstimateTreeDto) => void) => {
    loadingSetter(true);
    const localStorageKey = draftKey(updatedTree.estimateId, updatedTree.treeId);
    await upsertAndSetOperations()
      .then(async () => {
        const newTree = await updateTree(navigate, updatedTree.estimateId, updatedTree.treeId, updatedTree!);
        setSavedTree(newTree);
        setUpdatedTree(estimateTreeToUpdatedTree(newTree));
        localStorage.removeItem(localStorageKey); // Clear on success
        console.log(`Cleared localStorage for tree ${updatedTree.treeId}`);
        action(newTree);
      })
      .catch(e => {
        logError('Error saving tree details', {}, e);
        enqueueSnackbar(`Error saving tree details: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
      .finally(() => loadingSetter(false));
  }

  const upsertAndSetOperations = () => {
    return Promise.all([
      upsertAndSetTreeOperations(),
      updateOperationMethods()
    ]);
  }

  const upsertAndSetTreeOperations = async () => {
    if(!updatedOperationsDetails) return;
    const operationIdsToDelete = savedOperationsDetails.filter((operation) => !updatedOperationsDetails.find(updatedOperation => updatedOperation?.id === operation.id)).map(o => o.id);
    const deletePromises = operationIdsToDelete.map(async id => await deleteTreeOperation(navigate, savedTree.estimateId, savedTree.id, id));
    await Promise.all(deletePromises);

    // We don't do this in parallel so the operations save in the correct order
    for(let operation of updatedOperationsDetails) {
      try {
        const newOperation = operation?.id
          ? await updateTreeOperation(navigate, savedTree.estimateId, savedTree.id, operation?.id, operation)
          : await createTreeOperation(navigate, savedTree.estimateId, savedTree.id, operation!)
        setSavedOperationsDetails(operations => {
          operations[selectedOperationIndex] = newOperation;
          return operations;
        });
      } catch (e) {
        logError('Error saving tree operation', {}, e as Error);
        enqueueSnackbar(`Error saving tree operation: ${e}`, {variant: 'error', autoHideDuration: 5000});
      }
    }
  }

  const updateOperationMethods = async () => {
    const operationMethodIdsToAdd = selectedOperationMethodIds.filter(methodId => !savedOperationMethods.find(savedMethod => savedMethod.customOperationMethodId === methodId))
    const operationMethodIdsToRemove = savedOperationMethods.filter(method => !selectedOperationMethodIds.find(slectedMethodId => slectedMethodId === method.customOperationMethodId)).map(method => method.customOperationMethodId);
    const update = {
      operationIdsToRemove: operationMethodIdsToRemove,
      operationIdsToAdd: operationMethodIdsToAdd
    }
    console.log(`updateOperationMethods: ${JSON.stringify(update)}`);
    await updateTreeOperationMethods(navigate, savedTree.estimateId, savedTree.id, update);
  }

  const openImageDialog = (imagePreview: ImagePreview) => {
    setSelectedImage(imagePreview);
    setImageDialogIsOpen(true);
  };

  const closeImageDialog = () => {
    setImageDialogIsOpen(false);
  };

  const handleDeleteTreeClick = () => {
    setDeleteDialogOpen(true);
  }

  const handleDeleteTreeConfirm = () => {
    setDeleteDialogOpen(false);
    setIsSaving(true);
    const localStorageKey = draftKey(savedTree.estimateId, savedTree.id);
    deleteTree(navigate, savedTree.estimateId, savedTree.id)
      .then(() => {
        localStorage.removeItem(localStorageKey); // Clear on success
        console.log(`Cleared localStorage for deleted tree ${savedTree.id}`);
        props.onTreeDelete();
      })
      .catch(e => {
        enqueueSnackbar('Error deleting tree', {variant: 'error', autoHideDuration: 5000})
        logError('Error deleting tree', {}, e);
      })
      .finally(() => {
        setIsSaving(false);
      })
  }

  const handleDeleteTreeClose = () => {
    setDeleteDialogOpen(false);
  }

  const handleNewWorkItem = async (workItemType: WorkItemType) => {
    await saveTreeAndExecute(setIsSaving, (updatedTree) => props.onNewWorkItem(updatedTree, workItemType))
  }

  const handleNewGate = async () => {
    await saveTreeAndExecute(setIsSaving, props.onNewGate)
  }

  const handleSaveAndReturnToEstimate = async () => {
    await saveTreeAndExecute(setIsFinishing, props.handleSaveAndReturnToEstimate)
  }

  const savedOperationMethodIds = savedOperationMethods.map(method => method.customOperationMethodId);
  // Only real photo links count as changes, and position is the map's to save (preparation never sends it).
  const comparableTree = ({imageIdsToAdd, imageIdsToRemove, lat, lng, ...tree}: UpdateEstimateTreeDto) => JSON.stringify({
    ...tree, imageIdsToAdd: imageIdsToAdd?.length ? imageIdsToAdd : undefined, imageIdsToRemove: imageIdsToRemove?.length ? imageIdsToRemove : undefined,
  });
  const preparationDirty = !!preparation && !operationIsLoading && (
    comparableTree(updatedTree) !== comparableTree(estimateTreeToUpdatedTree(savedTree))
    || JSON.stringify(updatedOperationsDetails) !== JSON.stringify(savedOperationsDetails)
    || JSON.stringify([...selectedOperationMethodIds].sort()) !== JSON.stringify([...savedOperationMethodIds].sort())
    || !sameFigures(figuresDraft, savedFigures));
  const figureChoices = preparation?.figures?.equipmentChoices ?? [];
  const hasLoader = figuresDraft.equipment.some(row => /loader/i.test(figureChoices.find(c => c.equipmentId === row.equipmentId)?.type ?? ''));
  const preparationValid = [updatedTree.height, updatedTree.width, updatedTree.trunkDiameter,
    updatedTree.additionalDetails?.DebrisPile_length].every(exactValueIsValid)
    && [updatedTree.additionalDetails?.TreeGroup_treeCount, updatedTree.additionalDetails?.Tree_trunkCount]
      .every(value => value === undefined || value === null || /^\d{1,4}$/.test(String(value).trim()));

  // What is still unanswered, from the unsaved edits, so the strip follows what staff type.
  const reviewFacts = preparation ? [
    ...missingFacts({
      workItemType: savedTree.workItemType, species: updatedTree.species, height: updatedTree.height, width: updatedTree.width,
      trunkDiameter: updatedTree.trunkDiameter, additionalDetails: updatedTree.additionalDetails,
      operations: updatedOperationsDetails.flatMap(op => op ? [{operationType: op.operationType, details: op.details}] : []),
      images: treeImagePreviews,
    }),
    ...(preparation.figures ? missingFigures(preparation.figures.item, figuresDraft) : []),
  ] : [];
  const [jump, setJump] = useState<{field: string, id: number} | null>(null);
  const goToFact = (fact: MissingFact) => {
    const index = fact.operationType ? updatedOperationsDetails.findIndex(op => op?.operationType === fact.operationType) : -1;
    if (index >= 0 && index !== selectedOperationIndex) {
      setSelectedOperationIndex_Internal(index);
      setSelectedUpdatedOperationDetails_Internal(updatedOperationsDetails[index]);
    }
    setJump(current => ({field: fact.field, id: (current?.id ?? 0) + 1}));
  };
  // After the render that switched operation, bring the control into view, focus it, and flash it.
  useEffect(() => {
    if (!jump) return;
    const control = scrollableRef.current?.querySelector<HTMLElement>(`[data-testid="${jump.field}"], [data-review-field="${jump.field}"]`);
    setJump(null);
    if (!control) return;
    const target = control.matches('input, textarea, button, [role="combobox"]')
      ? control : control.querySelector<HTMLElement>('input:not([type="hidden"]), textarea, [role="combobox"], button');
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    (target ?? control).scrollIntoView({block: 'center', behavior: reduced ? 'auto' : 'smooth'});
    target?.focus({preventScroll: true});
    const box = (target ?? control).closest<HTMLElement>('.MuiFormControl-root, [data-review-field]') ?? control;
    box.animate?.([{boxShadow: '0 0 0 3px rgba(46, 125, 50, 0.6)'}, {boxShadow: '0 0 0 3px rgba(46, 125, 50, 0)'}], {duration: 1800});
  }, [jump]);

  const reportPreparation = (state: PreparationSaveState) => {
    setPreparationState(state);
    preparation?.onStateChange?.(state);
  };

  useEffect(() => {
    if (!preparation) return;
    // Decide outside a state updater: telling the page from inside one is a
    // setState during another component's render (React warns).
    const current = preparationState;
    const next: PreparationSaveState =
      current.kind === 'saving' || current.kind === 'error' ? current
        : preparationDirty ? {kind: 'dirty'} : current.kind === 'saved' ? current : {kind: 'clean'};
    if (next.kind !== current.kind) {
      setPreparationState(next);
      preparation.onStateChange?.(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preparationDirty, preparationState.kind]);

  const savePreparation = async (after?: () => void) => {
    if (!preparation || preparationState.kind === 'saving' || !preparationReady.current || !preparationValid) return;
    if (draftRevision.current !== preparation.revision) {
      reportPreparation({kind: 'error', stale: true, message: 'Someone else changed this work item. Reload it before saving; your draft is kept.'});
      return;
    }
    if (!preparationDirty) {
      after?.();
      return;
    }
    reportPreparation({kind: 'saving'});
    try {
      const {tree, revision} = await preparation.save({
        revision: draftRevision.current,
        savedTree,
        updatedTree,
        savedOperations: savedOperationsDetails,
        operations: updatedOperationsDetails.filter((op): op is InputEstimateTreeOperationDto => !!op),
        operationMethodIds: selectedOperationMethodIds,
        savedOperationMethodIds,
        figures: figuresDraft,
        savedFigures,
      });
      draftRevision.current = revision;
      setSavedFigures(figuresDraft);
      const operations = [...(tree.operations ?? [])].sort((a, b) => a.id - b.id);
      setSavedTree(tree);
      setUpdatedTree(estimateTreeToUpdatedTree(tree));
      setTreeImagePreviews(tree.images.map(image => ({id: image.id, s3Url: image.imageUrl})));
      setSavedOperationsDetails(operations);
      setUpdatedOperationsDetails(operations);
      setSelectedUpdatedOperationDetails_Internal(operations[selectedOperationIndex] ?? operations[0]);
      setSavedOperationMethods(selectedOperationMethodIds.map(id => ({customOperationMethodId: id} as EstimateTreeOperationMethodDto)));
      localStorage.removeItem(draftKey(tree.estimateId, tree.id));
      reportPreparation({kind: 'saved'});
      after?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      const stale = (e as {stale?: boolean})?.stale === true;
      reportPreparation({kind: 'error', message, stale});
    }
  };

  // A request made before this item was opened belongs to another item.
  const handledSaveRequest = useRef(preparation?.saveRequest?.id);
  useEffect(() => {
    const request = preparation?.saveRequest;
    if (!request || request.id === handledSaveRequest.current) return;
    handledSaveRequest.current = request.id;
    void savePreparation(request.then);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preparation?.saveRequest?.id]);

  const allRequiredValuesSet = () => {
    const treeValuesSet = updatedTree.height && updatedTree.width && updatedTree.trunkDiameter;
    const operationValuesSet = allOperationsValid();
    return treeValuesSet && operationValuesSet;
  }

  return (
    <Card
      ref={scrollableRef}
      sx={preparation
        ? { height: '100%', display: 'flex', flexDirection: 'column', overflow: {xs: 'visible', md: 'hidden'} }  // narrow: the page scrolls, so pinned photos can stick
        // While a photo is open over it, this window steps out of sight: it is
        // the photo window's width and starts higher, so its top edge and
        // scroll bar read as a strip on the photo (SQ-562).
        : { maxWidth: 600, maxHeight: '90vh', mx: 'auto', my: 4, overflowY: 'auto', visibility: imageDialogIsOpen || photoViewerOpen ? 'hidden' : undefined }}>
      <CardContent sx={preparation ? {flex: 1, overflowY: {xs: 'visible', md: 'auto'}} : undefined}>
        {preparation?.onPrevious && preparation.previousLabel && <ScrollPastCue edge="start" label={preparation.previousLabel} onGo={preparation.onPrevious}/>}
        <CardHeader title={`Work Item ${props.treeLocalId ?? ''} (${preparation ? workItemTypeLabel[savedTree.workItemType] ?? savedTree.workItemType : savedTree.workItemType})`}
                    action={preparation?.onChangeType && (
                      // Like adding or deleting an operation, the change works on the saved work item.
                      <Box component="span" title={preparationState.kind === 'saving' ? 'Saving this work item…'
                        : preparationDirty || preparationState.kind === 'error' ? 'Save this work item first: changing its type works on the saved work item.' : undefined}>
                        <Button size="small" variant="outlined" onClick={preparation.onChangeType} data-testid="change-type"
                                disabled={!canEdit || preparationDirty || preparationState.kind === 'error'}>
                          Change type
                        </Button>
                      </Box>
                    )}
                    subheader={preparation ? (preparation.figures
                      ? 'Library preparation · facts, time and equipment; the price and the invoice are not changed'
                      : 'Library preparation · facts only; price and time are not changed') : undefined}/>
        {operationIsLoading ? <CenteredCircularSpinner sx={{minHeight: '950px'}}/> :
        <Stack spacing={2}>
          {preparation && <ReviewStrip facts={reviewFacts} onGo={goToFact}/>}
          <EstimateTreeDetailImagesControls
            savedTree={savedTree}
            setUpdatedTree={setUpdatedTree}
            treeImagePreviews={treeImagePreviews}
            setTreeImagePreviews={setTreeImagePreviews}
            openImageDialog={openImageDialog}
            imageDialogOpen={imageDialogIsOpen}
            onViewerOpenChange={setPhotoViewerOpen}
            countTreeImagesLoading={countTreeImagesLoading}
            handleCompressedTreeImage={handleCompressedTreeImage}
            canEdit={canEdit}
            measuredPictureEnabled={!preparation}
          />
          {preparation?.photoSource && (() => {
            const have = new Set(treeImagePreviews.map(image => image.id));
            const offered = preparation.photoSource.images.filter(image => !have.has(image.id));
            return (
              <Stack spacing={1} data-testid="photo-source">
                <Typography variant="body2" color="text.secondary">
                  {offered.length
                    ? `Photos on ${preparation.photoSource.label}. Click one to add it to this stump.`
                    : `Every photo on ${preparation.photoSource.label} is on this stump.`}
                </Typography>
                {offered.length > 0 && (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {offered.map(image => (
                      <button key={image.id} type="button" disabled={!canEdit} data-testid={`photo-source-${image.id}`}
                              aria-label="Add this photo to the stump"
                              onClick={() => updateTreeImagesInState([image])}
                              style={{padding: 0, border: '1px solid rgba(0,0,0,0.23)', borderRadius: 4, background: 'none', cursor: canEdit ? 'pointer' : 'default', lineHeight: 0}}>
                        <img src={image.s3Url} alt="" width={96} height={96} style={{objectFit: 'cover', borderRadius: 3, display: 'block'}}/>
                      </button>
                    ))}
                  </Stack>
                )}
              </Stack>
            );
          })()}
          <EstimateTreeDetailOperations
            company={props.company}
            savedTree={savedTree}
            setUpdatedTree={setUpdatedTree}
            updatedOperationsDetails={updatedOperationsDetails}
            setUpdatedOperationsDetails={setUpdatedOperationsDetails}
            selectedOperationIndex={selectedOperationIndex}
            setSelectedOperationIndex_Internal={setSelectedOperationIndex_Internal}
            selectedUpdatedOperationDetails={selectedUpdatedOperationDetails}
            setSelectedUpdatedOperationDetails={setSelectedUpdatedOperationDetails}
            setSelectedUpdatedOperationDetails_Internal={setSelectedUpdatedOperationDetails_Internal}
            selectedOperationMethodIds={selectedOperationMethodIds}
            setSelectedOperationMethodIds={setSelectedOperationMethodIds}
            availableOperationMethods={availableOperationMethods}
            operationIsLoading={operationIsLoading}
            isSaving={isSaving}
            canEdit={canEdit}
            factsOnly={!!preparation}
            onDeleteOperation={preparation?.onDeleteOperation && (() => {
              const id = selectedUpdatedOperationDetails?.id;
              if (id !== undefined) preparation.onDeleteOperation?.(id);
            })}
            onAddOperation={preparation?.onAddOperation}
            operationActionsHold={!preparation ? null
              : preparationState.kind === 'saving' ? 'Saving this work item…'
              : preparationDirty || preparationState.kind === 'error' ? 'Save this work item first: adding or deleting an operation works on the saved work item.'
              : null}
            retiredDetails={preparation?.figures?.item
              ? Object.fromEntries(preparation.figures.item.operations.map(op => [op.operationId, op.retiredDetails]))
              : undefined}
            onOperationSwap={(previous, next) => {
              // Words typed as the old kind's details (a custom prune's description, a task's details) have no
              // field on the new kind; they go into the operation's note rather than vanish with the swap. When a
              // swap back restores such text to its own field, the copy leaves the note again.
              const id = next.id;
              if (id === undefined) return;
              const orphaned = orphanedOperationText(previous.operationType, next.operationType, previous.details);
              const restored = orphanedOperationText(next.operationType, previous.operationType, next.details);
              if (orphaned.length === 0 && restored.length === 0) return;
              setFiguresDraft(current => {
                const draft = current.operations[id] ?? {actualSeconds: null, notes: ''};
                return {...current, operations: {...current.operations, [id]: {...draft, notes: noteWith(draft.notes, orphaned, restored)}}};
              });
            }}
          />

          <Divider orientation="horizontal" flexItem />

          <EstimateTreeDetailDataFields
            savedTree={savedTree}
            updatedTree={updatedTree}
            setUpdatedTree={setUpdatedTree}
            canEdit={canEdit}
            exact={preparation ? {
              operationTypes: updatedOperationsDetails.filter(Boolean).map(op => op!.operationType),
              inferred: preparation.inferred,
              workPlan: preparation.figures?.workPlan,
              hasLoader,
            } : undefined}
          />
          {preparation?.figures && <EquipmentRowsEditor rows={figuresDraft.equipment} saved={preparation.figures.item?.equipment ?? []}
            choices={preparation.figures.equipmentChoices} disabled={!canEdit}
            laborCost={preparation.figures.item?.operations.reduce((sum, op) => sum + (op.figures?.estimatedCost ?? 0), 0)}
            laborRatePerHour={preparation.figures.laborRatePerHour}
            onChange={equipment => {
              setFiguresDraft(draft => ({...draft, equipment}));
              // The library's "equipment used" fact follows the rows: a machine with billed or actual time was used.
              const equipmentUsed = equipmentTypesUsed(equipment, preparation.figures?.item?.equipment ?? [], preparation.figures?.equipmentChoices ?? []);
              setUpdatedTree(tree => ({...tree, additionalDetails: {...tree.additionalDetails, equipmentUsed}}));
            }}/>}
          {preparation && selectedUpdatedOperationDetails && <PhotoContextFields
            operation={selectedUpdatedOperationDetails} disabled={!canEdit}
            photos={treeImagePreviews.map(photo => ({id: photo.id, url: photo.s3Url}))}
            inferred={preparation.inferredContext?.[selectedUpdatedOperationDetails.id ?? -1] ?? {}}
            onChange={(key, value) => setSelectedUpdatedOperationDetails(op => ({...op!, details: {...op!.details, [key]: value}}))}/>}
          {preparation?.figures && selectedUpdatedOperationDetails?.id !== undefined && (() => {
            const id = selectedUpdatedOperationDetails.id!;
            const saved = preparation.figures.item?.operations.find(op => op.operationId === id)?.figures;
            const draft = figuresDraft.operations[id] ?? {actualSeconds: null, notes: ''};
            return <OperationFiguresFields operationId={id} operationType={selectedUpdatedOperationDetails.operationType}
              saved={saved} draft={draft} disabled={!canEdit}
              crewSize={preparation.figures.crewSize} laborRatePerHour={preparation.figures.laborRatePerHour}
              onChange={next => setFiguresDraft(current => ({...current, operations: {...current.operations, [id]: next}}))}/>;
          })()}
          {preparation?.onAdvance && preparation.nextLabel && <ScrollPastCue edge="end" label={preparation.nextLabel} onGo={preparation.onAdvance}/>}
          {!preparation && <TextField
            disabled={!canEdit}
            label="Crew Notes"
            variant="outlined"
            multiline
            inputProps={{autoCapitalize: 'sentences'}}
            rows={2}
            value={updatedTree?.crewNotes ?? ''}
            onChange={handleCrewNotesChange}
          />}

          {!preparation && <>
          <Button onClick={handleDeleteTreeClick} variant="contained" color="error" style={{color: 'black'}} component="label" disabled={!canEdit}>
            {isSaving ? <CircularProgress/> : 'Delete'}
          </Button>
          {canEdit && <SaveAndAddNewComponent disabled={!allRequiredValuesSet()} isSaving={isSaving} onNewWorkItem={handleNewWorkItem} onNewGate={handleNewGate} />}
          {canEdit 
            ? <Button disabled={!allRequiredValuesSet()} onClick={handleSaveAndReturnToEstimate} variant="contained" component="label">{isFinishing ? <CircularProgress/> : 'Save and Return To Estimate'}</Button>
            : <Button onClick={handleSaveAndReturnToEstimate} variant="outlined" component="label">Return To Estimate</Button>
          }
          </>}
        </Stack>}
      </CardContent>
      {preparation && (
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', flexWrap: 'wrap', rowGap: 1}}>
          <Typography variant="body2" role="status" aria-live="polite" sx={{flexBasis: '100%'}}
                      color={preparationState.kind === 'error' ? 'error' : preparationState.kind === 'dirty' ? 'warning.dark' : 'text.secondary'}>
            {preparationState.kind === 'saving' ? 'Saving…'
              : preparationState.kind === 'saved' ? '✓ All changes saved'
              : preparationState.kind === 'dirty' ? (preparationValid ? 'Unsaved changes' : 'Fix the highlighted values to save')
              : preparationState.kind === 'error' ? 'Not saved' : 'No changes'}
          </Typography>
          <Button variant="outlined" sx={{mr: 'auto'}} disabled={!preparation.onPrevious || preparationState.kind === 'saving'} onClick={() => preparation.onPrevious?.()}>Previous</Button>
          {(preparation.onNext || preparation.onFinish) && <Button variant="outlined" disabled={!canEdit || !preparationDirty || !preparationValid}
                  onClick={() => savePreparation()}>Save</Button>}
          <Button variant="contained" disabled={!canEdit || !preparationValid || (!preparation.onNext && !preparation.onFinish && !preparationDirty)}
                  onClick={() => savePreparation(preparation.onNext ?? preparation.onFinish)}>
            {preparationState.kind === 'saving' ? <CircularProgress size={22} color="inherit"/>
              : preparation.onNext ? 'Save & next'
              : preparation.onFinish ? (preparationDirty ? 'Save & review approval' : 'Review approval')
              : 'Save'}
          </Button>
          {preparationState.kind === 'error' && (
            <Alert severity="error" sx={{width: '100%'}}
                   action={preparationState.stale
                     ? <Button color="inherit" size="small" onClick={() => { localStorage.removeItem(draftKey(savedTree.estimateId, savedTree.id)); preparation.reload(); }}>Reload item</Button>
                     : <Button color="inherit" size="small" onClick={() => savePreparation()}>Retry</Button>}>
              {preparationState.message} Your changes are kept on this page.
            </Alert>
          )}
        </Stack>
      )}
      <Dialog
        open={imageDialogIsOpen}
        onClose={closeImageDialog}
        fullScreen
        disableEscapeKeyDown
        PaperProps={PHOTO_DIALOG_PAPER}
      >
        <DrawableImage image={selectedImage} onSave={handleImageUpdated} isSaving={treeImageIsUpdating || operationImageIsUpdating} onClose={closeImageDialog} readonly={!canEdit} />
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={handleDeleteTreeClose}>
        <DialogTitle>Delete Work Item?</DialogTitle>
        <DialogActions sx={{ justifyContent: 'space-around', padding: '8px' }}>
          <Button onClick={handleDeleteTreeClose} variant="contained" color="primary" style={{color: 'black'}}>
            Cancel
          </Button>
          <Button onClick={handleDeleteTreeConfirm} variant="contained" color="error" style={{color: 'black'}} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

export default EstimateTreeDetail;
